import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { FAQS } from "../content/siteContent";
import { locFaq } from "../content/i18n_extra";

export function FaqSection({ extra = [] }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(null);
  const dynItems = extra.map((f, i) => ({ q: lang === "tr" ? f.q_tr : f.q_en || f.q_tr, a: lang === "tr" ? f.a_tr : f.a_en || f.a_tr, key: `dyn-${i}` }));

  return (
    <section id="sss" className="bg-ice py-28 sm:py-36" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="text-center">
          <div className="text-xs font-heading font-bold tracking-[0.18em] sm:tracking-[0.3em] uppercase text-brand mb-4">{t("sec_faq_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("sec_faq_title")}</h2>
        </motion.div>

        <div className="mt-12 space-y-3">
          {FAQS.map((f, i) => {
            const c = locFaq(f, i, lang);
            const isOpen = open === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.3) }}
                className="bg-white rounded-2xl border border-black/5 overflow-hidden"
              >
                <button
                  data-testid={`faq-q-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-heading font-bold text-sm sm:text-base text-ink">{c.q}</span>
                  <ChevronDown size={18} className={`text-brand shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="px-6 pb-6 text-sm text-ink/65 leading-relaxed" data-testid={`faq-a-${i}`}>{c.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
          {dynItems.map((c, di) => {
            const idx = `d${di}`;
            const isOpen = open === idx;
            return (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white rounded-2xl border border-brand/15 overflow-hidden"
                data-testid={`faq-dyn-${di}`}
              >
                <button
                  data-testid={`faq-dyn-q-${di}`}
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-heading font-bold text-sm sm:text-base text-ink">{c.q}</span>
                  <ChevronDown size={18} className={`text-brand shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="px-6 pb-6 text-sm text-ink/65 leading-relaxed" data-testid={`faq-dyn-a-${di}`}>{c.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}